import { useState } from 'react';
import {
  shipFromSelectOptions,
  type FacilityChoice,
} from '~/utils/facilityOptions';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select } from '~/components/ui';
import { CurrencySelector } from '~/components/ui/inputs/CurrencySelector';
import { MIN_POSITIVE_PRICE, MAX_PRICE } from '~/utils/moneyBounds';
import { Currency } from '~/graphql/graphql';
import { QuoteItemGroups } from './QuoteItemGroups';
import { NoShipFromFacilityNotice } from './NoShipFromFacilityNotice';
import {
  quoteResponseResetKey,
  quoteResponseSubmission,
  rowsMissingQuantity,
  useQuoteResponseDraft,
  type QuoteResponseSeed,
  type QuoteResponseSubmission,
} from './useQuoteResponseDraft';

export function QuoteResponseForm({
  quoteId,
  seed,
  facilities,
  submitting,
  disabled,
  hasFixedLines,
  usesWholeRequestPrice,
  onSubmit,
  onSaveDraft,
  onInvalid,
  linkedOrganizationId,
}: Readonly<{
  quoteId: string;
  seed: QuoteResponseSeed;
  facilities: FacilityChoice[];
  submitting: boolean;
  disabled: boolean;
  hasFixedLines: boolean;
  usesWholeRequestPrice: boolean;
  onSubmit: (submission: QuoteResponseSubmission) => void;
  onSaveDraft: (submission: QuoteResponseSubmission) => void;
  onInvalid: (errorKey: string) => void;
  linkedOrganizationId?: string;
}>) {
  const { t } = useTranslation('rfq-view');

  const resetKey = quoteResponseResetKey(quoteId, seed.groups, seed.items);

  const { draft, patch, patchRow, addRow, withdrawRow, dropRow, errorKey } =
    useQuoteResponseDraft(seed, resetKey, usesWholeRequestPrice, facilities);

  const [failedSubmitQuoteId, setFailedSubmitQuoteId] = useState<string | null>(
    null
  );
  const submitAttempted = failedSubmitQuoteId === quoteId;

  const fieldsLocked = submitting || disabled;

  const buildSubmission = (submit: boolean) =>
    quoteResponseSubmission(draft, usesWholeRequestPrice, submit);

  const shipFromOptions = shipFromSelectOptions(
    facilities,
    t('rfq_view.ship_from_placeholder')
  );

  const handleSubmit = () => {
    if (errorKey) {
      setFailedSubmitQuoteId(quoteId);
      onInvalid(errorKey);
      return;
    }
    onSubmit(buildSubmission(true));
  };

  const handleSaveDraft = () => {
    onSaveDraft(buildSubmission(false));
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-4 flex-wrap border-b border-neut-border pb-5 mb-5">
        <div className="max-w-xs">
          {facilities.length === 0 ? (
            <NoShipFromFacilityNotice
              linkedOrganizationId={linkedOrganizationId}
            />
          ) : (
            <Select
              name="ship-from"
              label={t('rfq_view.ship_from_label')}
              value={draft.sellingOrganizationFacilityId}
              disabled={fieldsLocked || facilities.length === 1}
              onChange={(_, value) =>
                patch({ sellingOrganizationFacilityId: value })
              }
              options={shipFromOptions}
            />
          )}
        </div>
        {usesWholeRequestPrice && (
          <>
            <div className="max-w-[160px]">
              <Input
                name="whole-request-pickup"
                type="number"
                min={MIN_POSITIVE_PRICE}
                max={MAX_PRICE}
                step="0.01"
                label={t('rfq_view.whole_request_pickup_price_label')}
                value={draft.wholeRequestPickupPrice}
                disabled={fieldsLocked}
                onChange={(_, value) =>
                  patch({ wholeRequestPickupPrice: value })
                }
              />
            </div>
            <div className="max-w-[160px]">
              <Input
                name="whole-request-delivery"
                type="number"
                min={MIN_POSITIVE_PRICE}
                max={MAX_PRICE}
                step="0.01"
                label={t('rfq_view.whole_request_delivery_price_label')}
                value={draft.wholeRequestDeliveryPrice}
                disabled={fieldsLocked}
                onChange={(_, value) =>
                  patch({ wholeRequestDeliveryPrice: value })
                }
              />
            </div>
          </>
        )}
        <div className="max-w-[160px]">
          <CurrencySelector
            name="currency"
            label={t('rfq_view.currency_label')}
            value={draft.currency}
            onChange={(_, value) => patch({ currency: value as Currency })}
            disabled={fieldsLocked}
          />
        </div>
        <div className="max-w-[160px]">
          <Input
            name="lead-time"
            type="number"
            min={1}
            step="1"
            label={t('rfq_view.lead_time_label')}
            value={draft.leadTimeDays}
            disabled={fieldsLocked}
            onChange={(_, value) => patch({ leadTimeDays: value })}
          />
        </div>
      </div>

      <QuoteItemGroups
        groups={seed.groups}
        draft={draft}
        disabled={fieldsLocked}
        hasFixedLines={hasFixedLines}
        usesWholeRequestPrice={usesWholeRequestPrice}
        rowIdsMissingQuantity={
          submitAttempted
            ? rowsMissingQuantity(draft, usesWholeRequestPrice)
            : new Set()
        }
        onPatchRow={patchRow}
        onAddRow={addRow}
        onRemoveRow={(platformProductLengthId, dropEntirely) =>
          dropEntirely
            ? dropRow(platformProductLengthId)
            : withdrawRow(platformProductLengthId)
        }
      />

      <div className="flex justify-end gap-4 mt-8">
        <Button
          name="save-edits"
          variant="secondary"
          size="sm"
          width="10rem"
          onClick={handleSaveDraft}
          disabled={fieldsLocked}
          loading={submitting}
        >
          {t('rfq_view.save_edits')}
        </Button>
        <Button
          name="submit-edits"
          variant="primary"
          size="sm"
          width="10rem"
          onClick={handleSubmit}
          disabled={fieldsLocked}
          loading={submitting}
        >
          {t('rfq_view.submit_edits')}
        </Button>
      </div>
    </div>
  );
}
